import { ucfirst } from "@huub-web/utils";

import type { TreeNode } from "../context/types";

export type DateGroup = {
  label: string;
  nodes: TreeNode[];
};

/**
 * Groups tree nodes by date bucket: today, weekday name (past 7 days),
 * or catch-all "earlier" label. Excludes soft-deleted nodes.
 */
export function groupTreeNodesByDate({
  nodes,
  locale,
  t,
}: {
  nodes: TreeNode[];
  locale: string;
  t: (key: string) => string;
}): DateGroup[] {
  const active = nodes
    .filter((n) => n.deleted_at === null)
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  if (active.length === 0) return [];

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sevenDaysAgo = new Date(todayStart.getTime() - 7 * 24 * 60 * 60_000);

  const weekdayFormatter = new Intl.DateTimeFormat(locale, {
    weekday: "long",
  });

  const groupMap = new Map<string, DateGroup>();

  for (const node of active) {
    const created = new Date(node.created_at);
    let label: string;

    if (created >= todayStart) {
      label = t("interiorDesigner.history.today");
    } else if (created >= sevenDaysAgo) {
      label = ucfirst(weekdayFormatter.format(created));
    } else {
      label = t("interiorDesigner.history.earlier");
    }

    const existing = groupMap.get(label);
    if (existing) {
      existing.nodes.push(node);
    } else {
      groupMap.set(label, { label, nodes: [node] });
    }
  }

  return Array.from(groupMap.values());
}
